import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

export default function PeriodsManager({ periods, divisions }) {
  const [periodList, setPeriodList] = useState(periods);
  const [newPeriod, setNewPeriod] = useState("");
  const [expandedPeriods, setExpandedPeriods] = useState([]);
  const [expandedDivisions, setExpandedDivisions] = useState([]);

  // Состояние для модалки подтверждения удаления
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  const handleAddPeriod = async () => {
    if (!newPeriod) {
      toast.error("Введите дату периода");
      return;
    }

    const response = await fetch("/settings/add_period", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ period: newPeriod }),
    });

    if (response.ok) {
      const addedPeriod = await response.json();
      setPeriodList([...periodList, addedPeriod]);
      setNewPeriod("");
      toast.success("Период успешно добавлен!");
    } else {
      toast.error("Ошибка при добавлении периода.");
    }
  };

  const handleDeletePeriod = async (periodId) => {
    const response = await fetch(`/settings/delete_period/${periodId}`, {
      method: "DELETE",
    });

    if (response.ok) {
      setPeriodList((prev) => prev.filter((p) => p.id !== periodId));
      toast.success("Период успешно удалён!");
    } else {
      toast.error("Ошибка при удалении периода.");
    }
  };

  const openDeleteConfirmation = (periodId) => {
    setPeriodToDelete(periodId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (periodToDelete) {
      handleDeletePeriod(periodToDelete);
    }
    setShowDeleteModal(false);
    setPeriodToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPeriodToDelete(null);
  };

  const togglePeriod = (id) => {
    setExpandedPeriods((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const toggleDivision = (id) => {
    setExpandedDivisions((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const updateUsersForPeriod = async (periodId, userIds) => {
    try {
      const response = await fetch("/settings/update_users_for_period", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ period_id: periodId, user_ids: userIds }),
      });

      if (response.ok) {
        const updatedPeriod = await response.json();
        setPeriodList((prev) =>
          prev.map((p) => (p.id === updatedPeriod.id ? updatedPeriod : p))
        );
        toast.success("Пользователи успешно обновлены!");
      } else {
        toast.error("Ошибка при обновлении пользователей.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при отправке запроса.");
    }
  };

  const toggleUser = (periodId, userId) => {
    const period = periodList.find((p) => p.id === periodId);
    const isSelected = period.opened_periods.some(
      (op) => op.user_id === userId
    );

    const updatedUserIds = isSelected
      ? period.opened_periods
          .filter((op) => op.user_id !== userId)
          .map((op) => op.user_id)
      : [...period.opened_periods.map((op) => op.user_id), userId];

    updateUsersForPeriod(periodId, updatedUserIds);
  };

  const selectAllUsers = (periodId) => {
    const allUsers = divisions
      .flatMap((division) => division.users)
      .map((user) => user.id);
    updateUsersForPeriod(periodId, allUsers);
  };

  const deselectAllUsers = (periodId) => {
    updateUsersForPeriod(periodId, []);
  };

  const selectDivisionUsers = (periodId, division) => {
    const period = periodList.find((p) => p.id === periodId);
    const divisionUserIds = division.users.map((user) => user.id);
    const currentUserIds = period.opened_periods.map((op) => op.user_id);

    const updatedUserIds = Array.from(
      new Set([...currentUserIds, ...divisionUserIds])
    );
    updateUsersForPeriod(periodId, updatedUserIds);
  };

  const deselectDivisionUsers = (periodId, division) => {
    const period = periodList.find((p) => p.id === periodId);
    const divisionUserIds = division.users.map((user) => user.id);

    const updatedUserIds = period.opened_periods
      .filter((op) => !divisionUserIds.includes(op.user_id))
      .map((op) => op.user_id);
    updateUsersForPeriod(periodId, updatedUserIds);
  };

  return (
    <div className="min-h-screen">
      <ToastContainer />
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h2 className="text-xl font-bold mb-4">Подтверждение удаления</h2>
            <p className="mb-6">
              Вы действительно хотите удалить этот период? Данное действие
              необратимо.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Отмена
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="container max-w-screen-2xl mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Управление периодами</h1>

        <div className="flex items-center mb-6">
          <input
            type="date"
            value={newPeriod}
            onChange={(e) => setNewPeriod(e.target.value)}
            className="p-2 border rounded mr-4 w-64"
          />
          <button
            onClick={handleAddPeriod}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Добавить период
          </button>
        </div>

        <div className="overflow-y-auto max-h-[500px] bg-gray-50 border rounded-lg shadow-inner p-4">
          {periodList.map((period) => (
            <div
              key={period.id}
              className="border rounded-lg bg-white shadow mb-4"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100"
                onClick={() => togglePeriod(period.id)}
              >
                <h2 className="text-xl font-semibold">{period.period}</h2>
                <div className="flex items-center space-x-4">
                  <button
                    className="text-blue-500 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectAllUsers(period.id);
                    }}
                  >
                    Выбрать всех
                  </button>
                  <button
                    className="text-red-500 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      deselectAllUsers(period.id);
                    }}
                  >
                    Снять всех
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteConfirmation(period.id);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                  <FontAwesomeIcon
                    icon={
                      expandedPeriods.includes(period.id)
                        ? faChevronDown
                        : faChevronRight
                    }
                  />
                </div>
              </div>

              {expandedPeriods.includes(period.id) && (
                <div className="p-4 space-y-4">
                  {divisions.map((division) => (
                    <div
                      key={division.id}
                      className="border rounded-lg bg-gray-50 p-2 shadow-sm"
                    >
                      <div
                        className="flex items-center justify-between p-2 cursor-pointer"
                        onClick={() => toggleDivision(division.id)}
                      >
                        <h3 className="text-md font-medium">
                          {division.name.trim()}
                        </h3>
                        <div className="space-x-4">
                          <button
                            className="text-blue-500 hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectDivisionUsers(period.id, division);
                            }}
                          >
                            Выбрать всех
                          </button>
                          <button
                            className="text-red-500 hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              deselectDivisionUsers(period.id, division);
                            }}
                          >
                            Снять всех
                          </button>
                        </div>
                      </div>

                      {expandedDivisions.includes(division.id) && (
                        <div className="p-2 space-y-2">
                          {division.users.map((user) => (
                            <label
                              key={user.id}
                              className="flex items-center justify-between px-2 py-1 border-b last:border-none"
                            >
                              <span className="text-sm">{user.realname}</span>
                              <input
                                type="checkbox"
                                checked={periodList
                                  .find((p) => p.id === period.id)
                                  .opened_periods.some(
                                    (op) => op.user_id === user.id
                                  )}
                                onChange={() => toggleUser(period.id, user.id)}
                                className="form-checkbox text-blue-600"
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
